import { loadableReady } from '@loadable/component';
import { ComponentType } from 'react';
import { hydrateRoot } from 'react-dom/client';

import { setConfigOnce } from 'config/appConfigUtils';
import { MainStoreCreateFromPartialState } from 'modules/main/store/MainStoreCreateFromPartialState';
import { RenderingClientInnerWrapper } from 'rendering/client/components/RenderingClientInnerWrapper';
import { RenderingWrapper } from 'rendering/components/RenderingWrapper';
import { renderingDeserializeHypernovaPayload } from 'rendering/hypernova/client/renderingDeserializeHypernovaPayload';

import { renderingBootstrapClient } from './utils/renderingBootstrapClient';
import { renderingClientPerformanceMark } from './utils/renderingClientPerformanceMark';
import { renderingLoadClientUserEnvironment } from './utils/renderingLoadClientUserEnvironment';

type Args = {
  Component: ComponentType<Record<string, never>>;
  createStore: MainStoreCreateFromPartialState;
};

export async function renderSsrClient({ Component, createStore }: Args) {
  const payload = renderingDeserializeHypernovaPayload();
  if (!payload) return;

  renderingClientPerformanceMark('Starting app');

  renderingClientPerformanceMark('Before waiting for loadables');
  await loadableReady();
  renderingClientPerformanceMark('Done waiting for loadables');

  renderingBootstrapClient();

  const {
    node,
    data: { state, config },
  } = payload;

  setConfigOnce(config);
  const store = createStore(state);

  renderingClientPerformanceMark('Starting hydration');
  hydrateRoot(
    node,
    <RenderingWrapper store={store} requestIsAmp={false}>
      <RenderingClientInnerWrapper>
        <Component />
      </RenderingClientInnerWrapper>
    </RenderingWrapper>,
  );
  renderingClientPerformanceMark('App ready');

  // We need to wait for the next "tick" before setting the user environment.
  //
  // If we don't do that we create a mismatch between the server and client
  // contents, causing a hydration issue and components skip the first render
  // after setting the user environment
  setTimeout(() => renderingLoadClientUserEnvironment({ store }), 1);
}
